import React from "react";
import { useLocation } from "react-router-dom";
import xavaLogo from "../../assets/images/xava-logo.png";

// Last line of defence: without this, ANY render error (a bad date, a missing
// field) unmounts the whole app and leaves a white screen. Also catches the
// chunk-load failures that happen when a deploy lands while a tab is open.
const isChunkError = (error) =>
  /Loading chunk|ChunkLoadError|dynamically imported module|Importing a module script failed/i.test(
    error?.message || error?.name || ""
  );

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("App error:", error, info?.componentStack);
    // A stale chunk means the app was redeployed under this tab — one silent
    // reload picks up the new build. Guard so we can never reload-loop.
    if (isChunkError(error)) {
      try {
        if (!sessionStorage.getItem("xv:chunkReloaded")) {
          sessionStorage.setItem("xv:chunkReloaded", "1");
          window.location.reload();
        }
      } catch (e) {
        /* storage blocked — fall through to the error card */
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Recover automatically once the user navigates somewhere else
    if (this.state.error && prevProps.routeKey !== this.props.routeKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const updating = isChunkError(error);

    return (
      <div className="xv-errpage">
        <div className="xv-errpage-card">
          <img src={xavaLogo} alt="Xava Web Services" className="xv-errpage-logo" />
          <h2>{updating ? "Xava CRM just updated" : "Something went wrong on this page"}</h2>
          <p>
            {updating
              ? "A new version was released while you had this open. Reload to pick it up."
              : "This page hit an unexpected error. Your work is safe. Reloading usually sorts it out. If it keeps happening, please tell HR what you were doing."}
          </p>
          <div className="xv-errpage-actions">
            <button
              type="button"
              className="xv-errpage-btn"
              onClick={() => {
                try {
                  sessionStorage.removeItem("xv:chunkReloaded");
                } catch (e) {}
                window.location.reload();
              }}
            >
              Reload page
            </button>
            <button
              type="button"
              className="xv-errpage-btn is-ghost"
              onClick={() => {
                this.setState({ error: null });
                window.history.back();
              }}
            >
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }
}

// Resets itself when the route changes, so one broken page doesn't strand the app
export function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary routeKey={location.pathname}>{children}</ErrorBoundary>;
}

export default ErrorBoundary;
